import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { AdReel } from "@/components/AdReel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, ShieldCheck, TrendingUp, PhoneCall, ArrowLeft } from "lucide-react";
import { DisclaimerFields, useDisclaimer } from "@/components/Disclaimer";

export default function Login() {
  const { login, register, forgotPassword, user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "", nmls_number: "", npn: "" });
  const [portal, setPortal] = useState("mortgage");
  const home = (u) => (u.role === "admin" ? "/admin" : u.role === "surety_agent" ? "/surety" : u.role === "property_manager" ? "/pm" : u.role === "realtor" ? "/realtor" : "/dashboard");
  const disclaimer = useDisclaimer(portal === "pm" || portal === "realtor" ? "pm" : "mlo");
  const [agree, setAgree] = useState(false);
  const [sigName, setSigName] = useState("");
  const [sigImage, setSigImage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav(home(user), { replace: true });
  }, [user, nav]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let res;
    if (mode === "signin") res = await login(form.email.trim(), form.password, portal);
    else if (mode === "signup") {
      const surety = portal === "surety";
      const noDisclaimer = surety;
      if (!noDisclaimer && (!agree || sigName.trim().length < 3 || !sigImage)) { setLoading(false); toast.error("Agree to the disclaimer, type your full legal name and sign it"); return; }
      res = await register({ ...form, email: form.email.trim(), portal: surety ? "surety" : portal === "pm" || portal === "realtor" ? portal : "", agree_disclaimer: agree, signature_name: sigName.trim(), signature_image: sigImage });
    }
    else res = await forgotPassword(form.email.trim(), portal);
    setLoading(false);

    if (!res.ok) { toast.error(res.error); return; }
    toast.dismiss();
    if (mode === "forgot") {
      toast.success(res.message || "If an account exists, a reset link has been sent.");
      setMode("signin");
      return;
    }
    if (res.pending) {
      toast.success(res.message || "Account created — awaiting administrator approval.");
      setMode("signin");
      return;
    }
    toast.success(mode === "signin" ? "Welcome back" : "Account created — welcome!");
    nav(home(res.user), { replace: true });
  };

  const title = mode === "signin" ? "Sign in" : mode === "signup" ? "Create your account" : "Reset your password";
  const subtitle = mode === "signin" ? "Choose your portal. Dually licensed? Use the same email for both, each with its own password."
    : mode === "signup" ? (portal === "surety" ? "Set up your Surety Bond Agent account to start contractor lead runs." : portal === "pm" ? "Set up your Property Manager account to find owner and Elite Rentals leads." : portal === "realtor" ? "Set up your Realtor account to find FSBO and expired listing leads and apply for REO listings." : "Set up your Loan Originator account to start prospecting.")
    : "Enter your email and we'll send you a reset link.";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between navy-panel p-12 text-white overflow-hidden">
        <img src="https://static.prod-images.emergentagent.com/jobs/0d7ee199-4c9a-4ee4-a802-6a8f9b597807/images/7ea03af674084eab559aaffd7b7a3fb5eef4b15ad8fda0fa4be0fc2bcf361c27.jpeg" alt="" aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 z-0 h-full w-2/3 object-cover opacity-20 [mask-image:linear-gradient(to_right,transparent,black)]" />
        <Brand light />
        <div className="relative z-10 max-w-md animate-fade-up">
          <h1 className="font-display text-4xl font-extrabold leading-tight">
            Mortgage prospecting,<br />
            <span className="text-amber-400">engineered for originators.</span>
          </h1>
          <p className="mt-4 text-slate-300 leading-relaxed">
            Launch a research run, get qualified mortgage prospects, and automate
            compliant outreach — all inside one private command center.
          </p>
          <div className="mt-10 space-y-4">
            {[
              { icon: TrendingUp, t: "Targeted prospecting runs", d: "HELOC, refi, purchase, DSCR & more" },
              { icon: PhoneCall, t: "AI calling with Ava", d: "Warm transfers and booked callbacks" },
              { icon: ShieldCheck, t: "Private & compliant", d: "Your prospects are never shared" },
            ].map((f) => (
              <div key={f.t} className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-amber-400/15 text-amber-300">
                  <f.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold">{f.t}</div>
                  <div className="text-sm text-slate-400">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
          <AdReel className="mt-6 aspect-video w-full max-w-[280px]" />
        </div>
        <div className="relative z-10 space-y-3">
          <a href="https://www.bbb.org/us/fl/orlando/profile/real-estate/elite-real-estate-finance-llc-0733-235980082/#sealclick" target="_blank" rel="noreferrer" data-testid="bbb-badge-login" className="inline-flex items-center gap-2 rounded-lg bg-white/95 px-3 py-2 shadow-sm">
            <img src="https://seal-centralflorida.bbb.org/seals/blue-seal-200-42-bbb-235980082.png" alt="Elite Real Estate & Finance LLC BBB Business Review" className="h-9 w-auto" />
            <span className="text-xs font-semibold leading-tight text-slate-800">A Rated<br />BBB Accredited Business</span>
          </a>
          <div className="text-xs text-slate-400">
            eliteleads.com · Elite Real Estate &amp; Finance LLC · NMLS #2863463
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-slate-50 p-6">
        <Card className="w-full max-w-md border-slate-200 p-8 shadow-lg">
          <div className="lg:hidden mb-6"><Brand /></div>
          <AdReel className="mb-6 aspect-video w-full lg:hidden" />
          <h2 className="font-display text-2xl font-extrabold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className={`grid ${mode === "signup" ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3 sm:grid-cols-5"} gap-1 rounded-xl bg-slate-100 p-1`} data-testid={mode === "signup" ? "signup-portal" : "login-portal"}>
              {[["mortgage", mode === "signup" ? "Mortgage loan officer" : "Mortgage"], ["surety", mode === "signup" ? "Surety bond agent" : "Surety Bonds"], ["pm", mode === "signup" ? "Property manager" : "Property Mgmt"], ["realtor", mode === "signup" ? "Realtor" : "Realtors"], ...(mode === "signup" ? [] : [["admin", "Admin"]])].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setPortal(k)} data-testid={`${mode === "signup" ? "signup" : "login"}-portal-${k}`}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${portal === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label htmlFor="first_name">First name</Label>
                  <Input id="first_name" value={form.first_name} data-testid="signup-first"
                    onChange={(e) => set("first_name", e.target.value)} className="mt-1.5" placeholder="Jordan" /></div>
                <div><Label htmlFor="last_name">Last name</Label>
                  <Input id="last_name" value={form.last_name} data-testid="signup-last"
                    onChange={(e) => set("last_name", e.target.value)} className="mt-1.5" placeholder="Rivera" /></div>
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} data-testid="login-email"
                onChange={(e) => set("email", e.target.value)} className="mt-1.5"
                placeholder="you@eliterealestatefinance.com" />
            </div>
            {mode !== "forgot" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {mode === "signin" && (
                    <button type="button" data-testid="forgot-password-link"
                      onClick={() => setMode("forgot")} className="text-xs font-semibold text-amber-700 hover:underline">
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input id="password" type="password" required value={form.password} data-testid="login-password"
                  onChange={(e) => set("password", e.target.value)} className="mt-1.5"
                  placeholder={mode === "signup" ? "At least 6 characters" : "••••••••"} />
              </div>
            )}
            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={form.phone} data-testid="signup-phone"
                    onChange={(e) => set("phone", e.target.value)} className="mt-1.5" placeholder="904-555-0100" /></div>
                {portal === "surety" ? <div><Label htmlFor="npn">NPN</Label>
                  <Input id="npn" value={form.npn} data-testid="signup-npn"
                    onChange={(e) => set("npn", e.target.value)} className="mt-1.5" placeholder="National Producer Number" /></div>
                : portal === "pm" || portal === "realtor" ? null : <div><Label htmlFor="nmls">NMLS #</Label>
                  <Input id="nmls" value={form.nmls_number} data-testid="signup-nmls"
                    onChange={(e) => set("nmls_number", e.target.value)} className="mt-1.5" placeholder="Optional" /></div>}
                {(portal === "mortgage" || portal === "pm" || portal === "realtor") && <div className="sm:col-span-2"><DisclaimerFields disclaimer={disclaimer} agree={agree} setAgree={setAgree} name={sigName} setName={setSigName} setSigImage={setSigImage} /></div>}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full gold-cta border-0 font-bold h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" />
                : mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
            </Button>
          </form>

          <div className="mt-5 text-center text-sm text-slate-500">
            {mode === "signin" && (
              <>Don't have an account?{" "}
                <button type="button" data-testid="switch-to-signup" onClick={() => { setMode("signup"); if (portal === "admin") setPortal("mortgage"); }}
                  className="font-semibold text-amber-700 hover:underline">Create one</button></>
            )}
            {mode === "signup" && (
              <>Already have an account?{" "}
                <button type="button" data-testid="switch-to-signin" onClick={() => setMode("signin")}
                  className="font-semibold text-amber-700 hover:underline">Sign in</button></>
            )}
            {mode === "forgot" && (
              <button type="button" data-testid="back-to-signin" onClick={() => setMode("signin")}
                className="inline-flex items-center gap-1 font-semibold text-amber-700 hover:underline">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in</button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
